import { Card } from '@/components/ui/card';
import { DataList } from '@/components/ui/data-list';
import { Text } from '@/components/ui/text';
import InventoryDetailsQuickAdd from '@/features/inventory/components/inventory-details-quick-add';
import { toLocaleString } from '@/lib/currency-helpers';
import { useLocale } from '@/lib/hooks/use-locale';
import type { RouterOutput } from '@/server/types/infer-trpc-types';

interface Props {
    canAddToDraft?: boolean;
    data: RouterOutput['platoUser']['getDetailProduct']['getProductDetail'];
}

export const InventoryDetailsInfo = ({ canAddToDraft, data: data }: Props) => {
    const { t, locale } = useLocale();

    const allIdentifiers: string[] = [];

    allIdentifiers.push(
        t('product_information__id__product_id', {
            ns: 'product-details',
            value: data.product_variant_id,
        }).toString(),
    );

    if (data.purchase_order_number) {
        allIdentifiers.push(
            t('product_information__id__purchase_order_number', {
                ns: 'product-details',
                value: data.purchase_order_number,
            }).toString(),
        );
    }

    if (data.ean) {
        allIdentifiers.push(
            t('product_information__id__ean', {
                ns: 'product-details',
                value: data.ean,
            }).toString(),
        );
    }

    if (data.gtin) {
        allIdentifiers.push(
            t('product_information__id__gtin', {
                ns: 'product-details',
                value: data.gtin,
            }).toString(),
        );
    }

    return (
        <Card>
            <div className="flex flex-col gap-3">
                <Text color="neutral-500">{allIdentifiers.join(' | ')}</Text>
                <Text weight="medium">{data.product_variant_name}</Text>
                <DataList>
                    {Boolean(data.article_group_id && data.article_group_name) && (
                        <DataList.Item>
                            <DataList.Label>
                                {t('info_block_title__product_group', { ns: 'product-details' })}
                            </DataList.Label>
                            <DataList.Value>{`${data.article_group_id}: ${data.article_group_name}`}</DataList.Value>
                        </DataList.Item>
                    )}
                    {Boolean(data.supplier_id && data.supplier_name) && (
                        <DataList.Item>
                            <DataList.Label>
                                {t('info_block_title__supplier', { ns: 'product-details' })}
                            </DataList.Label>
                            <DataList.Value>{`${data.supplier_id}: ${data.supplier_name}`}</DataList.Value>
                        </DataList.Item>
                    )}
                    {typeof data.total_availability === 'number' && (
                        <DataList.Item>
                            <DataList.Label>
                                {t('info_block_title__total_availability', { ns: 'product-details' })}
                            </DataList.Label>
                            <DataList.Value>{toLocaleString(data.total_availability, locale)}</DataList.Value>
                        </DataList.Item>
                    )}
                    {data.sales_units_per_package_unit && data.package_unit ? (
                        <DataList.Item>
                            <DataList.Label>
                                {t('info_block_title__packaging', { ns: 'product-details' })}
                            </DataList.Label>
                            <DataList.Value>
                                {toLocaleString(data.sales_units_per_package_unit, locale)}
                                &nbsp;{data.quantity_unit_sales}
                                {data.package_unit && data.package_unit !== data.quantity_unit_sales
                                    ? ` / ${data.package_unit}`
                                    : null}
                            </DataList.Value>
                        </DataList.Item>
                    ) : null}
                </DataList>
                {canAddToDraft && <InventoryDetailsQuickAdd product={data} />}
            </div>
        </Card>
    );
};
